/* Capo's Den Build Studio engine.
 * One engine for every studio line (car / weapon / jewelry); each base is a JSON config asset.
 * Images: layered renders per view, composited in linear light: out = spec + colour * paint.
 * Never loads model files - images only. */
(() => {
  const toLin = new Float32Array(256);
  for (let i = 0; i < 256; i++) toLin[i] = Math.pow(i / 255, 2.2);
  // linear -> sRGB byte lookup (CPU fallback only; the GPU path does this in a shader)
  const toSrgb = new Uint8ClampedArray(4097);
  for (let i = 0; i <= 4096; i++) toSrgb[i] = Math.round(Math.pow(i / 4096, 1 / 2.2) * 255);
  const hexLin = (h) => { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.pow(v / 255, 2.2)); };
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (n) => '$' + (Math.round(n * 100) / 100).toString();
  const clamp01 = (v) => Math.min(1, Math.max(0, v));
  const hexHsv = (hex) => {
    const n = parseInt(hex.slice(1), 16), r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    let h = 0;
    if (d) h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return [(h * 60 + 360) % 360, mx ? d / mx : 0, mx];
  };
  const hsvHex = (h, s, v) => {
    const f = (k) => { const x = (k + h / 60) % 6; return v - v * s * Math.max(0, Math.min(x, 4 - x, 1)); };
    return '#' + [f(5), f(3), f(1)].map((c) => Math.round(c * 255).toString(16).padStart(2, '0')).join('');
  };
  // extra preview colours offered on every car (paint is set in-game, so these cost nothing)
  // shortcut row at the top of the mod menu (common asks; ids missing from a car's menu are skipped)
  const QUICK = ['debadge', 'tint', 'wheelswap', 'livery', 'handling', 'starlights', 'ambient'];
  const EXTRA = [['Candy Red', '#9e0a14'], ['Torch Red', '#c4151c'], ['Mango', '#e3651d'], ['Canary Yellow', '#f2c40f'], ['Lime', '#7bc043'],
    ['Racing Green', '#0f3d2e'], ['Teal', '#0f7c80'], ['Electric Blue', '#1f5fd6'], ['Midnight Purple', '#2d1b4e'], ['Plum', '#5a2a6e'],
    ['Hot Pink', '#e0218a'], ['Rose Gold', '#b76e79'], ['Bronze', '#8c5a2b'], ['Champagne', '#c8b48a'], ['Cement Grey', '#8a8d8f'], ['Gunmetal', '#3b3f45']];
  const I = (d) => `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
  const ICON = {
    performance: I('<path d="M4 16a8 8 0 1 1 16 0"/><path d="M12 16l4-5"/>'),
    protection: I('<path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/>'),
    body: I('<path d="M3 15l2-5 4-3h6l4 3 2 5v3H3z"/><circle cx="7.5" cy="18" r="1.5"/><circle cx="16.5" cy="18" r="1.5"/>'),
    animated: I('<path d="M20 12a8 8 0 1 1-2.3-5.7"/><path d="M20 4v4h-4"/>'),
    lighting: I('<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 0-4 10.5c.7.7 1 1.5 1 2.5h6c0-1 .3-1.8 1-2.5A6 6 0 0 0 12 3z"/>'),
    paintbooth: I('<path d="M18 3l3 3-9 9-4 1 1-4z"/><path d="M4 21c2 0 4-1 4-3"/>'),
    interior: I('<path d="M7 4h6l1 9H8z"/><path d="M6 13h10l1 4H6z"/><path d="M8 17v3M15 17v3"/>'),
    plates: I('<rect x="3" y="7" width="18" height="10" rx="2"/><path d="M7 12h10"/>'),
    wild: I('<path d="M5 19c0-3 2-5 4-6l6-6c2-2 4-2 4-2s0 2-2 4l-6 6c-1 2-3 4-6 4z"/><path d="M9 13l2 2"/>'),
    files: I('<path d="M14 3H6v18h12V7z"/><path d="M14 3v4h4"/><path d="M9 13h6M9 17h6"/>'),
    wheels: I('<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="2.5"/><path d="M12 4v5.5M12 14.5V20M4 12h5.5M14.5 12H20"/>'),
    modeling: I('<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M12 12l8-4.5M12 12v9M12 12L4 7.5"/>'),
    car: I('<path d="M12 4v16M4 12h16"/>'),
  };

  class BuildStudio {
    constructor(root) {
      this.root = root;
      this.base = root.dataset.assetBase;
      this.drawing = 0;
      this.spin = { sig: '', frames: [], warming: '', dragging: false, x0: 0, f0: 0, auto: true, timer: null };
      this.cv = this.q('canvas');
      // phones / low-memory devices: smaller canvas + spin frames and a tighter cache of decoded layers
      this.lite = matchMedia('(max-width: 990px)').matches || (navigator.deviceMemory || 8) <= 4;
      if (this.lite) { this.cv.width = 1200; this.cv.height = 675; }
      // GPU compositor when available (instant paint changes); otherwise the CPU path below
      this.gl = window.CaposGL ? window.CaposGL.get() : null;
      this.ctx = this.cv.getContext('2d', { willReadFrequently: !this.gl });
      this.W = this.cv.width; this.H = this.cv.height;
      this.spinScale = this.lite ? 2 / 3 : 1;
      this.budget = (this.lite ? 48 : 256) * 1024 * 1024;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.stopAuto(); });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stopAuto(); });
      new IntersectionObserver(entries => { root.classList.toggle('bs-in', entries[0].isIntersecting); if (!entries[0].isIntersecting) this.stopAuto(); }, {threshold:0.1}).observe(root);
      this.bindSpin();
      // phones: the build total + request bar sits at the bottom of the screen until the real summary scrolls into view
      const foot = this.q('.bs-foot');
      if (foot && this.q('.bs-mbar')) new IntersectionObserver((en) => root.classList.toggle('bs-foot-in', en[0].isIntersecting)).observe(foot);
      document.addEventListener('fullscreenchange', () => { this.q('.bs-canvas')?.classList.toggle('bs-is-full', document.fullscreenElement === this.q('.bs-canvas')); this.syncFull(); });
      document.addEventListener('keydown', (e) => { const c = this.q('.bs-canvas.bs-max'); if (e.key === 'Escape' && c) { c.classList.remove('bs-max'); this.syncFull(); } });
      root.addEventListener('click', (e) => this.onClick(e));
      root.addEventListener('keydown', (e) => { const inf = e.target.closest && e.target.closest('[data-demo]'); if (inf && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); this.openDemo(inf.dataset.demo); } });
      root.addEventListener('input', (e) => this.onInput(e));
      root.addEventListener('change', (e) => { const t = e.target; if (('custom' in t.dataset || 'cpHue' in t.dataset) && this.S?.custom) this.pushRecent(this.S.custom); });
      // custom colour square: drag to pick saturation (x) and brightness (y)
      root.addEventListener('pointerdown', (e) => { const sv = e.target.closest('[data-cp-sv]'); if (!sv || !this.cfg) return;
        e.preventDefault(); sv.setPointerCapture(e.pointerId); this.svDrag = sv; this.svMove(e); });
      root.addEventListener('pointermove', (e) => { if (this.svDrag) this.svMove(e); });
      const svEnd = () => { if (this.svDrag) { this.svDrag = null; this.pushRecent(this.S.custom); } };
      root.addEventListener('pointerup', svEnd); root.addEventListener('pointercancel', svEnd);
      root.addEventListener('keydown', (e) => { if (!e.target.matches?.('[data-cp-sv]')) return;
        const k = { ArrowLeft: [-0.02, 0], ArrowRight: [0.02, 0], ArrowUp: [0, 0.02], ArrowDown: [0, -0.02] }[e.key]; if (!k) return;
        e.preventDefault(); const [h, sat, v] = this.hsv; this.hsv = [h, clamp01(sat + k[0]), clamp01(v + k[1])]; this.setCustom(hsvHex(...this.hsv), 'sv'); });
      if (root.dataset.garageUrl) { this.garage = new Image(); this.garage.crossOrigin = 'anonymous'; this.garage.src = root.dataset.garageUrl;
        this.garage.onload = () => { if (this.cfg) { this.spin.frames = []; this.spin.sig = ''; this.draw(); } }; } // reflections need the room
      try { this.bases = JSON.parse(this.q('[data-bs-bases]')?.textContent || '[]'); } catch (e) { this.bases = []; }
      this.embed = root.dataset.bsEmbed || ''; // set on the homepage copy: holds the full studio URL
      let want = this.embed ? null : new URLSearchParams(location.search).get('base');
      try { want = want || (this.embed ? null : JSON.parse(decodeURIComponent(location.hash.slice(1))).base); } catch (e) { /* no saved build */ }
      const start = this.bases.find((b) => b.id === want);
      // a link naming a car we don't have: say so and let the customer pick, instead of silently opening another car
      if (want && !start && this.bases.length) this.pickCar();
      else this.loadBase(start ? start.url : root.dataset.configUrl);
    }
    pickCar() {
      const ld = this.q('[data-bs-loading]');
      ld.hidden = false; ld.classList.add('bs-pick');
      this.root.querySelectorAll('[data-bs-quote]').forEach((q) => (q.disabled = true)); this.q('[data-bs-views]').innerHTML = '';
      ld.innerHTML = `<div style="text-align:center;letter-spacing:normal;color:#ececec;padding:20px"><p style="margin:0 0 6px;font-size:16px">That car isn't in the Build Studio yet. Pick one:</p><div class="bs-bases" style="justify-content:center">${
        this.bases.map((b) => `<button type="button" data-base="${esc(b.id)}">${esc(b.label)}</button>`).join('')}</div></div>`;
      if (!this.embed) history.replaceState(null, '', location.pathname + location.search.replace(/([?&])base=[^&]*&?/, '$1').replace(/[?&]$/, ''));
    }
    q(s) { return this.root.querySelector(s); }

    loadBase(url) {
      const version = this.loadVersion = (this.loadVersion || 0) + 1;
      this.stopAuto();
      this.root.querySelectorAll('[data-bs-quote]').forEach((q) => (q.disabled = true));
      this.q('[data-bs-loading]').hidden = false;
      this.q('[data-bs-loading]').classList.remove('bs-pick');
      this.q('[data-bs-loading]').textContent = 'LOADING RENDERS…';
      // the shared mod menu (one price list for every car) loads once, alongside the car's own config
      this.mods = this.mods || (this.root.dataset.modsUrl ? fetch(this.root.dataset.modsUrl).then((r) => r.json()).catch(() => null) : Promise.resolve(null));
      this.wheelsP = this.wheelsP || (this.root.dataset.wheelsUrl ? fetch(this.root.dataset.wheelsUrl).then((r) => r.json()).catch(() => []) : Promise.resolve([]));
      Promise.all([fetch(url).then((r) => r.json()), this.mods, this.wheelsP]).then(([cfg, mods, wc]) => { this.wcat = wc || []; if (version === this.loadVersion) this.init(cfg, mods); })
        .catch(() => { if (version === this.loadVersion) this.q('[data-bs-loading]').textContent = 'Studio unavailable right now.'; });
    }

    init(cfg, mods) {
      this.wbase = (this.root.dataset.wheelsUrl || '').split('?')[0].replace(/[^/]*$/, '');
      // a step with "mods": true takes its options from the shared menu (minus the car's mods_exclude), after any car-specific options
      for (const st of cfg.steps) if (st.mods && mods) {
        // the full Mod Shop menu, Wheels and Modeling lab included; a car with its own wheels step already prices the wheel swap there
        const hasWheels = cfg.steps.some((s) => s.id === 'wheels' && s.type === 'single');
        const skip = new Set([...(cfg.mods_exclude || []), ...(hasWheels ? ['wheelswap'] : [])]), own = (st.options || []).map((o) => ({ ...o, group: o.group || st.label }));
        const groups = mods.groups;
        st.groups = [...new Set(own.map((o) => o.group))].map((label) => ({ id: 'car', label })).concat(groups.map((g) => ({ id: g.id, label: g.label, note: g.note })));
        st.options = own.concat(...groups.map((g) => g.options.filter((o) => !skip.has(o.id)).map((o) => ({ ...o, group: g.label }))));
        st.groups = st.groups.filter((g) => st.options.some((o) => o.group === g.label));
      }
      this.cfg = cfg; this.root.classList.toggle('bs-room', cfg.lit === 'room'); this.stock = false; this.cat = null; this.find = ''; this.hsv = null; // mod menu: open category / search text
      this.root.querySelectorAll('[data-bs-quote]').forEach((q) => (q.disabled = false));
      this.cache = new Map(); this.cacheBytes = 0;
      this.stopAuto(); this.spin.auto = false; this.spin.sig = ''; this.spin.frames = [];
      const single = Object.fromEntries(cfg.steps.filter((s) => s.type === 'single').map((s) => [s.id, s.options[0].id]));
      this.S = { view: cfg.views[0].id, frame: 3, paint: 0, custom: null, pick: single, up: new Set(), qty: {}, ask: {}, tab: 0,
                 discord: '', fw: (cfg.frameworks || [''])[0], limits: '', notes: '' };
      try {
        const h = this.embed ? null : JSON.parse(decodeURIComponent(location.hash.slice(1)));
        if (h && h.base === cfg.id) this.restore(h);
      } catch (e) { /* no saved build */ }
      if (!cfg.views.some((v) => v.id === this.S.view)) this.S.view = cfg.views[0].id;
      if (this.S.tab >= cfg.steps.length) this.S.tab = 0;
      this.q('[data-bs-title]').innerHTML = esc(cfg.title) + (cfg.subtitle ? ' <span>' + esc(cfg.subtitle) + '</span>' : '');
      // car picker cards: a small still of each base (assets/bs-thumb-<id>.webp); the label alone if the still is missing
      this.q('[data-bs-bases-row]').innerHTML = this.bases.length > 1 ? this.bases.map((b) =>
        `<button type="button" class="bs-basecard ${b.id === cfg.id ? 'is-on' : ''}" data-base="${esc(b.id)}"><img src="${esc(this.base)}bs-thumb-${esc(b.id)}.webp" alt="" width="160" height="90" loading="lazy" onerror="this.remove()"><span>${esc(b.label)}</span></button>`).join('') : '';
      this.q('[data-bs-bases-row] .is-on')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.render();
      this.draw().then(() => this.prefetch());
    }

    /* ---------- share links ----------
     * The URL hash is untrusted input: only whitelisted fields are restored, each validated against the config.
     * Customer details (Discord, framework, limits, notes) are never written to or read from the link. */
    restore(h) {
      const cfg = this.cfg, S = this.S, int = (v, max) => (Number.isInteger(v) && v >= 0 && v < max ? v : null);
      const view = cfg.views.find((v) => v.id === h.view);
      if (view) S.view = view.id;
      const spin = cfg.views.find((v) => v.type === 'spin');
      if (spin && int(h.frame, spin.frames) !== null) S.frame = h.frame;
      if (int(h.paint, cfg.paints.length) !== null) S.paint = h.paint;
      S.custom = typeof h.custom === 'string' && /^#[0-9a-f]{6}$/i.test(h.custom) ? h.custom.toLowerCase() : null;
      if (int(h.tab, cfg.steps.length) !== null) S.tab = h.tab;
      for (const st of cfg.steps) {
        if (st.type === 'single' && h.pick && (st.options.some((o) => o.id === h.pick[st.id]) || (st.catalog && this.wcOpt(h.pick[st.id])))) S.pick[st.id] = h.pick[st.id];
        if (st.type === 'multi' && Array.isArray(h.up)) for (const o of st.options) if (h.up.includes(o.id)) S.up.add(o.id);
      }
    }
    shareState() {
      const S = this.S;
      return { base: this.cfg.id, view: S.view, frame: S.frame, paint: S.paint, custom: S.custom, pick: S.pick, up: [...S.up], tab: S.tab };
    }

    /* ---------- images ---------- */
    // cfg.rev: bump whenever renders are replaced so browsers/CDN don't serve stale layers
    url(view, layer) { return this.base + `${this.cfg.asset_prefix}-${view}-${layer}.webp` + (this.cfg.rev ? `?rev=${this.cfg.rev}` : ''); }
    // decoded layer pixels, least-recently-used first; evicted past this.budget bytes (re-decoding hits the HTTP cache)
    load(view, layer, w, h) {
      const key = `${view}/${layer}@${w}`, hit = this.cache.get(key);
      if (hit) { this.cache.delete(key); this.cache.set(key, hit); return hit.p; }
      const entry = { bytes: 0 };
      entry.p = new Promise((res) => {
        const im = new Image(); im.crossOrigin = 'anonymous'; im.decoding = 'async';
        im.onload = () => { const c = document.createElement('canvas'); c.width = w; c.height = h;
          const x = c.getContext('2d'); x.drawImage(im, 0, 0, w, h); const d = x.getImageData(0, 0, w, h).data;
          if (this.cache.get(key) === entry) { entry.bytes = d.length; this.cacheBytes += d.length; this.evict(); }
          res(d); };
        im.onerror = () => res(null);
        im.src = this.url(view, layer);
      });
      this.cache.set(key, entry);
      return entry.p;
    }
    evict() {
      for (const [k, e] of this.cache) {
        if (this.cacheBytes <= this.budget || this.cache.size <= 1) break;
        this.cacheBytes -= e.bytes; this.cache.delete(k);
      }
    }
    async layer(view, name, w, h) {
      const painted = this.cfg.painted.includes(name);
      if (this.gl) {
        const [spec, paint] = await Promise.all([window.CaposGL.image(this.url(view, name)), painted ? window.CaposGL.image(this.url(view, name + '_paint')) : null]);
        return { spec, paint };
      }
      const spec = await this.load(view, name, w, h);
      const paint = painted ? await this.load(view, name + '_paint', w, h) : null;
      return { spec, paint };
    }
    // draw composed layers into a 2D context; false if the GPU went away (caller redraws on the CPU path)
    paintTo(layers, W, H, x) {
      if (this.gl) {
        const c = this.gl.compose(layers, W, H, this.colour(), this.cfg.lit ? 0 : undefined);
        if (c) { this.place(c, x, W, H); return true; }
        this.gl = null; this.spin.frames = []; this.spin.sig = ''; return false;
      }
      const t = this.cpuTmp || (this.cpuTmp = document.createElement('canvas')); t.width = W; t.height = H;
      t.getContext('2d').putImageData(this.compose(layers, W, H, x), 0, 0); this.place(t, x, W, H); return true;
    }
    // room renders already contain the workshop, floor and shadows (car rendered in place): draw as-is
    place(src, x, W, H) {
      if (this.cfg.lit === 'room') { x.clearRect(0, 0, W, H); x.drawImage(src, 0, 0, W, H); return; }
      this.ground(src, x, W, H);
    }
    // put the car in the garage rather than in front of it: ambient occlusion under the body, a blurred mirror on the
    // concrete, the room's ceiling (hex lights, beams) reflected in the roof, hood and glass, and the room's light falloff
    ground(src, x, W, H) {
      x.clearRect(0, 0, W, H);
      const p = this.probe || (this.probe = document.createElement('canvas')); p.width = 160; p.height = 90;
      const pc = p.getContext('2d', { willReadFrequently: true }); pc.clearRect(0, 0, 160, 90); pc.drawImage(src, 0, 0, 160, 90);
      const d = pc.getImageData(0, 0, 160, 90).data; let top = 90, bot = -1, lft = 160, rgt = -1;
      for (let i = 3; i < d.length; i += 4) if (d[i] > 230) { // opaque body only, not the baked soft shadow
        const k = (i - 3) / 4, r = (k / 160) | 0, c = k % 160;
        if (r < top) top = r; if (r > bot) bot = r; if (c < lft) lft = c; if (c > rgt) rgt = c; }
      if (bot <= top) { x.drawImage(src, 0, 0, W, H); return; }
      const sx = W / 160, sy = H / 90, y0 = (bot + 1) * sy, yT = top * sy, h = y0 - yT, xL = lft * sx, xR = (rgt + 1) * sx, cx = (xL + xR) / 2, w = xR - xL;
      // 1. contact shadow / occlusion: the floor under the car is darker than anything around it
      x.save(); x.translate(cx, y0 - h * 0.05); x.scale(1, 0.11);
      const ao = x.createRadialGradient(0, 0, 0, 0, 0, w * 0.55);
      ao.addColorStop(0, 'rgba(0,0,0,0.62)'); ao.addColorStop(0.6, 'rgba(0,0,0,0.32)'); ao.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = ao; x.beginPath(); x.arc(0, 0, w * 0.55, 0, Math.PI * 2); x.fill(); x.restore();
      // 2. the car mirrored in the sealed concrete, softened like a real semi-gloss floor
      const m = this.mirror || (this.mirror = document.createElement('canvas')); m.width = W; m.height = H;
      const mc = m.getContext('2d'); mc.clearRect(0, 0, W, H);
      mc.save(); mc.filter = 'blur(' + Math.max(1, W / 500) + 'px)'; mc.translate(0, 2 * y0); mc.scale(1, -1); mc.drawImage(src, 0, 0, W, H); mc.restore();
      mc.globalCompositeOperation = 'destination-in';
      const f = mc.createLinearGradient(0, y0, 0, y0 + h * 0.5); f.addColorStop(0, 'rgba(0,0,0,0.34)'); f.addColorStop(1, 'rgba(0,0,0,0)');
      mc.fillStyle = f; mc.fillRect(0, y0, W, h * 0.5); mc.globalCompositeOperation = 'source-over';
      x.drawImage(m, 0, 0);
      // 3. the car itself
      x.drawImage(src, 0, 0, W, H);
      // 4. the garage ceiling reflected in the upper body: flipped strip of the backdrop, kept to the car, faded down the doors
      const g = this.garage;
      if (this.cfg.lit !== 'garage' && g && g.complete && g.naturalWidth) { // garage-lit renders reflect the real room already
        const e = this.env || (this.env = document.createElement('canvas')); e.width = W; e.height = H;
        const ec = e.getContext('2d'); ec.clearRect(0, 0, W, H);
        const gw = g.naturalWidth, gh = g.naturalHeight;
        ec.save(); ec.filter = 'blur(' + Math.max(1, W / 900) + 'px)';
        ec.translate(0, yT + h * 0.62); ec.scale(1, -1);
        ec.drawImage(g, 0, gh * 0.12, gw, gh * 0.26, xL - w * 0.15, 0, w * 1.3, h * 0.62); ec.restore();
        ec.globalCompositeOperation = 'destination-in'; ec.drawImage(src, 0, 0, W, H);
        const fade = ec.createLinearGradient(0, yT, 0, yT + h * 0.62);
        fade.addColorStop(0, 'rgba(0,0,0,1)'); fade.addColorStop(1, 'rgba(0,0,0,0)');
        ec.fillStyle = fade; ec.fillRect(0, 0, W, H);
        x.save(); x.globalCompositeOperation = 'screen'; x.globalAlpha = 0.34; x.drawImage(e, 0, 0); x.restore();
      }
      // 5. the room's light: brighter roof under the fixtures, darker sills near the floor
      x.globalCompositeOperation = 'source-atop';
      const l = x.createLinearGradient(0, yT, 0, y0);
      const lit = this.cfg.lit === 'garage';
      l.addColorStop(0, 'rgba(255,246,240,' + (lit ? 0 : 0.04) + ')'); l.addColorStop(0.5, 'rgba(0,0,0,0)'); l.addColorStop(1, 'rgba(6,6,10,' + (lit ? 0.1 : 0.22) + ')');
      x.fillStyle = l; x.fillRect(0, 0, W, H); x.globalCompositeOperation = 'source-over';
    }
    layerNames() { return ['base', ...this.cfg.layer_order.map((step) => { const st = this.cfg.steps.find((s) => s.id === step); return this.stock || !st.options.some((o) => o.id === this.S.pick[step]) ? st.options[0].id : this.S.pick[step]; })]; }
    // shop wheels (build-studio-wheels.json) pickable on any car's wheel step; quoted in the ticket
    wcOpt(id) { const w = (this.wcat || []).find((x) => x.id === id); return w && { id: w.id, name: w.name, note: 'From our wheel shop, fitted on request', quote: true, img: w.img, url: w.url }; }
    opt(st) { return st.options.find((o) => o.id === this.S.pick[st.id]) || (st.catalog && this.wcOpt(this.S.pick[st.id])) || st.options[0]; }
    // before/after: the car with its first (stock) wheel pick and first factory paint; any change to the build ends the comparison
    setStock(on) {
      this.stock = !!on; const b = this.q('[data-bs-compare]'), tag = this.q('[data-bs-cmp-tag]');
      if (b) { b.classList.toggle('is-on', this.stock); b.setAttribute('aria-pressed', String(this.stock)); b.dataset.label = this.stock ? 'Show my build' : 'Compare with stock'; }
      if (tag) tag.hidden = !this.stock;
      this.cv.classList.add('bs-fade'); this.draw();
    }
    colour() { return this.stock ? this.cfg.paints[0][1] : this.S.custom || this.cfg.paints[this.S.paint][1]; }
    compose(layers, W, H, ctx) {
      const col = hexLin(this.colour()), coat = this.cfg.lit ? 0 : window.CaposGL ? CaposGL.coat(this.colour()) : 0.4;
      const out = ctx.createImageData(W, H), o = out.data, acc = new Float32Array(W * H * 4);
      for (const L of layers) {
        const s = L.spec, p = L.paint; if (!s) continue;
        for (let i = 0; i < s.length; i += 4) {
          const a = s[i + 3] / 255; if (a === 0) continue;
          let r = toLin[s[i]] * a, g = toLin[s[i + 1]] * a, b = toLin[s[i + 2]] * a;
          if (p && p[i + 3]) { const pa = p[i + 3] / 255, cc = 1 - coat * pa; r *= cc; g *= cc; b *= cc; r += col[0] * toLin[p[i]] * pa; g += col[1] * toLin[p[i + 1]] * pa; b += col[2] * toLin[p[i + 2]] * pa; }
          const k = 1 - a;
          acc[i] = r + acc[i] * k; acc[i + 1] = g + acc[i + 1] * k; acc[i + 2] = b + acc[i + 2] * k; acc[i + 3] = a + acc[i + 3] * k;
        }
      }
      for (let j = 0; j < acc.length; j += 4) {
        const a = acc[j + 3]; if (a <= 0) { o[j + 3] = 0; continue; }
        const k = 4096 / a;
        o[j] = toSrgb[Math.min(4096, (acc[j] * k) | 0)]; o[j + 1] = toSrgb[Math.min(4096, (acc[j + 1] * k) | 0)];
        o[j + 2] = toSrgb[Math.min(4096, (acc[j + 2] * k) | 0)]; o[j + 3] = a * 255;
      }
      return out;
    }
    viewCfg() { return this.cfg ? this.cfg.views.find((v) => v.id === this.S.view) : {}; }
    // a view with "needs": {"mod": id, "ask": choice} only exists while that service (and that choice) is in the build,
    // e.g. the doors-up still for Door conversion: Scissor. "ask_default": true also covers "no choice made yet".
    viewOk(v) {
      const n = v.needs; if (!n) return true;
      if (!this.S.up.has(n.mod)) return false;
      const a = this.S.ask[n.mod] || '';
      return !n.ask || a === n.ask || (!a && n.ask_default);
    }
    views() { return this.cfg.views.filter((v) => this.viewOk(v)); }
    // after a service toggle / choice: jump to the view that shows it, or leave a view that no longer applies
    showFor(mod, prev) {
      const v = this.cfg.views.find((x) => x.needs && x.needs.mod === mod && this.viewOk(x));
      if (v) { this.S.view = v.id; this.stopAuto(); this.render(); this.draw(); }
      else if (this.S.view !== prev) this.draw(); // render() already left a view that no longer applies
    }

    async draw() {
      const my = ++this.drawing, v = this.viewCfg();
      if (v.type === 'spin') return this.drawSpin(my, v);
      const layers = await Promise.all(this.layerNames().map((n) => this.layer(v.id, n, this.W, this.H)));
      if (my !== this.drawing) return;
      if (!this.paintTo(layers, this.W, this.H, this.ctx)) return this.draw();
      this.shown();
    }
    shown() {
      this.syncRot();
      this.q('[data-bs-loading]').hidden = true;
      this.cv.classList.remove('bs-fade');
      this.saveLink();
    }
    // keep the share link in step with every state change (also called from render, so upgrade toggles update it at once)
    saveLink() {
      const hash = '#' + encodeURIComponent(JSON.stringify(this.shareState()));
      if (!this.embed) { history.replaceState(null, '', hash); return; }
      this.embedLink = new URL(this.embed, location.origin).href.split('#')[0] + hash;
      document.querySelectorAll('[data-bs-open-studio]').forEach((a) => (a.href = this.embedLink)); // "Open the full Studio" keeps the build
    }
    link() { return this.embed ? this.embedLink || new URL(this.embed, location.origin).href : location.href; }
    prefetch() {
      // phones: only the current view's options; desktop: every fixed view
      for (const v of this.views().filter((x) => x.type !== 'spin' && (!this.lite || x.id === this.S.view)))
        for (const st of this.cfg.steps.filter((s) => s.type === 'single'))
          for (const o of st.options) this.layer(v.id, o.id, this.W, this.H);
    }

    /* ---------- 360 spin: compose each frame once per build, then dragging swaps bitmaps ---------- */
    frameView(v, i) { return v.prefix + String(i).padStart(2, '0'); }
    async spinFrame(v, i, sig) {
      if (this.spin.sig === sig && this.spin.frames[i]) return this.spin.frames[i];
      const w = Math.round(v.w * this.spinScale), h = Math.round(v.h * this.spinScale);
      const layers = await Promise.all(this.layerNames().map((n) => this.layer(this.frameView(v, i), n, w, h)));
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const x = c.getContext('2d'); if (!this.paintTo(layers, w, h, x)) return this.spinFrame(v, i, sig);
      if (this.spin.sig === sig) this.spin.frames[i] = c;
      return c;
    }
    async drawSpin(my, v) {
      const sig = [...this.layerNames(), this.colour()].join('|');
      if (this.spin.sig !== sig) { this.spin.sig = sig; this.spin.frames = []; }
      const c = await this.spinFrame(v, this.S.frame, sig);
      if (my !== this.drawing || this.S.view !== v.id) return;
      this.ctx.clearRect(0, 0, this.W, this.H); this.ctx.drawImage(c, 0, 0, this.W, this.H);
      this.shown();
      if (this.spin.warming !== sig) (async () => {
        this.spin.warming = sig;
        // walk the ring from where we started; S.frame moves while auto-rotate runs, so don't index from it
        const start = this.S.frame;
        for (let k = 1; k < v.frames; k++) {
          if (this.spin.sig !== sig || this.S.view !== v.id) break;
          const i = (start + k) % v.frames;
          if (!this.spin.frames[i]) { await this.spinFrame(v, i, sig); await new Promise((r) => setTimeout(r, 0)); }
        }
        if (this.spin.warming === sig) this.spin.warming = '';
      })();
      if (this.spin.auto && !this.spin.timer) this.spin.timer = setInterval(() => {
        if (this.S.view !== v.id) return this.stopAuto();
        const nx = (this.S.frame + 1) % v.frames;
        if (this.spin.frames[nx]) { this.S.frame = nx; this.draw(); }
        else if (this.spin.pending !== nx) { // not composed yet (or its layer failed once): build it now rather than stall
          this.spin.pending = nx;
          this.spinFrame(v, nx, this.spin.sig).finally(() => { if (this.spin.pending === nx) this.spin.pending = null; });
        }
      }, 110);
    }
    stopAuto() { this.spin.auto = false; clearInterval(this.spin.timer); this.spin.timer = null; const b=this.q('[data-spin-toggle]'); if(b){b.textContent='Play rotation';b.setAttribute('aria-pressed','false');} }
    bindSpin() {
      const cv = this.cv, sp = this.spin;
      cv.tabIndex = 0;
      cv.addEventListener('keydown', e => {
        if (!this.cfg) return;
        const v=this.viewCfg();
        if(v.type!=='spin'||!['ArrowLeft','ArrowRight','Home','End'].includes(e.key))return;
        e.preventDefault();this.stopAuto();
        this.S.frame=e.key==='Home'?0:e.key==='End'?v.frames-1:(this.S.frame+(e.key==='ArrowRight'?1:-1)+v.frames)%v.frames;
        this.draw();
      });
      cv.addEventListener('pointerdown', (e) => { if(!this.cfg || e.button > 0)return; const sv = this.spinView(); if (!sv) return;
        this.stopAuto();
        if (this.S.view !== sv.id) { this.S.view = sv.id; this.render(); this.draw(); } // grabbing a still view picks up the 360 at the current angle
        sp.dragging = true; sp.x0 = e.clientX; sp.f0 = this.S.frame; cv.setPointerCapture(e.pointerId); });
      cv.addEventListener('pointermove', (e) => { if (!sp.dragging) return; const v = this.viewCfg(), n = v.frames;
        const step = cv.getBoundingClientRect().width / (n * 2);
        const f = (((sp.f0 - Math.round((e.clientX - sp.x0) / step)) % n) + n) % n;
        if (f !== this.S.frame) { this.S.frame = f; this.draw(); } });
      const up = () => (sp.dragging = false);
      cv.addEventListener('pointerup', up); cv.addEventListener('pointercancel', up);
    }

    /* ---------- pricing ---------- */
    stepPrice(st) {
      if (st.type === 'single') { const o = this.opt(st); return o.quote ? 0 : o.price || 0; }
      if (st.type === 'multi') return st.options.filter((o) => this.S.up.has(o.id) && !o.quote).reduce((t, o) => t + (o.price || 0) * this.qty(o), 0);
      return 0;
    }
    total() { return this.cfg.base_price + this.cfg.steps.reduce((t, st) => t + this.stepPrice(st), 0); }
    // chosen items priced in the ticket (quote) or with a "from" price: the total is then a starting price
    openEnded() { return this.cfg.steps.some((st) => (st.type === 'multi' && st.options.some((o) => this.S.up.has(o.id) && this.isEst(o))) || (st.type === 'single' && this.opt(st).quote)); }
    totalText() { return money(this.total()) + (this.openEnded() ? '+' : ''); }
    priceText(o, plus) {
      if (o.per && this.qty(o) > 1 && o.price && !o.quote) return (plus ? '+' : '') + money(o.price * this.qty(o)) + (/from/.test(o.price_label || '') ? '+' : '');
      return o.quote ? 'Quoted in ticket' : o.price_label ? o.price_label : o.price ? (plus ? '+' : '') + money(o.price) : 'Free';
    }
    // ranges, "from" prices and quotes are estimates until the ticket confirms them
    isEst(o) { return !!(o.quote || /from|–|-/.test(o.price_label || ''));
    }
    qty(o) { return o.per ? Math.min(20, Math.max(1, this.S.qty[o.id] || 1)) : 1; }
    paintName() {
      if (!this.S.custom) return this.cfg.paints[this.S.paint][0];
      const named = EXTRA.find((x) => x[1] === this.S.custom);
      return named ? named[0] : 'Custom ' + this.S.custom.toUpperCase();
    }

    /* ---------- UI ---------- */
    render() {
      const active = this.root.contains(document.activeElement) ? document.activeElement : null;
      const focusData = active?.tagName === 'BUTTON' ? Object.entries(active.dataset) : null;
      const cfg = this.cfg, S = this.S, st = cfg.steps[S.tab];
      if (!this.viewOk(this.viewCfg())) S.view = cfg.views[0].id;
      const spin = this.viewCfg().type === 'spin';
      this.q('[data-bs-views]').innerHTML = this.views().map((v) =>
        `<button type="button" class="${S.view === v.id ? 'is-on' : ''}" data-view="${v.id}">${esc(v.label)}</button>`).join('');
      const turn = !!this.spinView(); this.q('[data-bs-hint]').hidden = !turn; this.cv.classList.toggle('bs-grab', turn);
      this.renderRot();
      this.cv.setAttribute('aria-label', spin ? 'Your build preview. Use Left and Right arrow keys to rotate; Home and End choose the first and last angle.' : 'Your build preview');
      if(spin)this.q('[data-bs-views]').insertAdjacentHTML('beforeend',`<button type="button" data-spin-toggle aria-pressed="${this.spin.auto}">${this.spin.auto?'Pause rotation':'Play rotation'}</button>`);
      this.q('[data-bs-tabs]').innerHTML = cfg.steps.map((s, i) =>
        `<button type="button" class="${S.tab === i ? 'is-on' : ''}" data-tab="${i}">${i + 1}. ${esc(s.label)}</button>`).join('');
      let h = `<h3>${esc(st.label)}</h3>` + (st.note ? `<div class="bs-note">${esc(st.note)}</div>` : '');
      if (st.type === 'paint') h += this.paintPane(); else if (st.type === 'single') {
        h += st.options.map((o) => `<button type="button" class="bs-opt ${S.pick[st.id] === o.id ? 'is-on' : ''}" data-pick="${st.id}:${o.id}">
          <span>${esc(o.name)}<small>${esc(o.note || '')}</small></span><span class="bs-pr">${o.price ? '+' + money(o.price) : 'Included'}</span></button>`).join('');
        if (st.catalog && this.wcat?.length) h += this.wheelGrid(st);
      } else if (st.type === 'multi') {
        h += this.modMenu(st);
      } else if (st.type === 'server') {
        h += `<label class="bs-f">Discord username *</label><input type="text" value="${esc(S.discord)}" placeholder="e.g. capo#0001" data-field="discord">
          <label class="bs-f">Framework *</label><select data-field="fw">${(cfg.frameworks || []).map((f) => `<option ${S.fw === f ? 'selected' : ''}>${esc(f)}</option>`).join('')}</select>
          <label class="bs-f">Server limits (poly / file size / LEO rules)</label><input type="text" value="${esc(S.limits)}" placeholder="e.g. under 300k polys, 16MB" data-field="limits">
          <label class="bs-f">Notes</label><textarea data-field="notes" placeholder="Anything else we should know">${esc(S.notes)}</textarea>`;
      }
      const pane = this.q('[data-bs-pane]'), keep = pane.scrollTop; pane.innerHTML = h; if (this.keepScroll) pane.scrollTop = keep; this.keepScroll = false;
      this.summary();
      this.saveLink();
      if (focusData?.length) [...this.root.querySelectorAll('button')].find(b => focusData.every(([k,v]) => b.dataset[k] === v))?.focus({preventScroll:true});
    }
    /* ---------- rotation bar: turn the car from any view (switches to the 360 view) ---------- */
    spinView() { return this.cfg.views.find((v) => v.type === 'spin'); }
    renderRot() {
      const el = this.q('[data-bs-rot]'), sv = this.spinView(); if (!el) return;
      if (!sv) { el.innerHTML = ''; return; }
      const svgL = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M4 12a8 8 0 1 0 2.3-5.7"/><path d="M4 4v5h5"/></svg>';
      el.innerHTML = `<button type="button" data-rot="-1" aria-label="Rotate left">${svgL}</button>
        <input type="range" min="0" max="${sv.frames - 1}" step="1" value="${this.S.frame}" data-rot-range aria-label="Rotation angle">
        <output data-rot-deg>${Math.round(this.S.frame * 360 / sv.frames)}°</output>
        <button type="button" data-rot="1" aria-label="Rotate right" class="bs-rot-r">${svgL}</button>`;
    }
    syncRot() {
      const sv = this.spinView(), r = this.q('[data-rot-range]'), o = this.q('[data-rot-deg]'); if (!sv || !r) return;
      const spin = this.S.view === sv.id; r.value = this.S.frame;
      r.closest('[data-bs-rot]').classList.toggle('is-live', spin);
      o.textContent = Math.round(this.S.frame * 360 / sv.frames) + '°';
    }
    rotateTo(frame) {
      const sv = this.spinView(); if (!sv) return;
      this.stopAuto();
      const n = sv.frames; this.S.frame = ((frame % n) + n) % n;
      if (this.S.view !== sv.id) { this.S.view = sv.id; this.cv.classList.add('bs-fade'); this.render(); }
      this.draw();
    }

    /* ---------- paint: factory colours, extra colours, custom picker, recent picks ---------- */
    paintPane() {
      const cfg = this.cfg, S = this.S, cur = this.colour();
      if (!this.hsv || hsvHex(...this.hsv) !== cur) this.hsv = hexHsv(cur);
      const [h, sat, v] = this.hsv, recent = this.recent();
      const sw = (list, factory) => list.map(([n, hex], i) => `<button type="button" title="${esc(n)}" aria-label="${esc(n)}" style="background:${hex}" ${factory
        ? `data-paint="${i}" class="${!S.custom && S.paint === i ? 'is-on' : ''}"` : `data-hex="${hex}" class="${S.custom === hex ? 'is-on' : ''}"`}></button>`).join('');
      return `<div class="bs-cname">${esc(this.paintName())}</div>
        <div class="bs-plabel">Factory colours</div><div class="bs-sw">${sw(cfg.paints, true)}</div>
        <div class="bs-plabel">More colours</div><div class="bs-sw bs-sw-sm">${sw(EXTRA, false)}</div>
        <div class="bs-plabel">Custom colour</div>
        <div class="bs-cp" data-cp style="--h:${Math.round(h)}">
          <div class="bs-cp-sv" data-cp-sv role="slider" tabindex="0" aria-label="Colour saturation and brightness" aria-valuetext="${cur.toUpperCase()}"><i style="left:${sat * 100}%;top:${(1 - v) * 100}%"></i></div>
          <input type="range" class="bs-cp-hue" min="0" max="359" value="${Math.round(h)}" data-cp-hue aria-label="Hue">
          <div class="bs-cp-row"><span class="bs-cp-chip" style="background:${cur}"></span>
            <input type="text" value="${cur.toUpperCase()}" maxlength="7" spellcheck="false" autocomplete="off" data-cp-hex aria-label="Hex colour code">
            <label class="bs-cp-native" title="Open your device's colour picker"><input type="color" value="${cur}" data-custom aria-label="Device colour picker"></label></div>
          ${recent.length ? `<div class="bs-plabel">Recent</div><div class="bs-sw bs-sw-sm">${sw(recent.map((x) => ['Recent ' + x.toUpperCase(), x]), false)}</div>` : ''}
        </div>`;
    }
    recent() { try { return (JSON.parse(localStorage.getItem('capos-bs-recent') || '[]') || []).filter((x) => /^#[0-9a-f]{6}$/.test(x)).slice(0, 10); } catch (e) { return []; } }
    pushRecent(hex) {
      if (!hex || !/^#[0-9a-f]{6}$/.test(hex) || EXTRA.some((x) => x[1] === hex)) return;
      try { localStorage.setItem('capos-bs-recent', JSON.stringify([hex, ...this.recent().filter((x) => x !== hex)].slice(0, 10))); } catch (e) { /* private mode */ }
    }
    svMove(e) {
      const r = this.svDrag.getBoundingClientRect();
      this.hsv = [this.hsv[0], clamp01((e.clientX - r.left) / r.width), 1 - clamp01((e.clientY - r.top) / r.height)];
      this.setCustom(hsvHex(...this.hsv), 'sv');
    }
    // live colour change without rebuilding the pane (keeps the drag / typing alive); the GPU redraw is coalesced per frame
    setCustom(hex, src) {
      hex = hex.toLowerCase(); this.S.custom = hex; if (this.stock) this.setStock(false);
      if (src !== 'sv' && src !== 'hue') this.hsv = hexHsv(hex);
      const name = this.q('.bs-cname'); if (name) name.textContent = this.paintName();
      this.root.querySelectorAll('.bs-pane .bs-sw button').forEach((b) => b.classList.toggle('is-on', b.dataset.hex === hex));
      const cp = this.q('[data-cp]');
      if (cp) {
        const [h, sat, v] = this.hsv; cp.style.setProperty('--h', Math.round(h));
        const k = cp.querySelector('.bs-cp-sv i'); k.style.left = sat * 100 + '%'; k.style.top = (1 - v) * 100 + '%';
        if (src !== 'hue') cp.querySelector('[data-cp-hue]').value = Math.round(h);
        if (src !== 'hex') cp.querySelector('[data-cp-hex]').value = hex.toUpperCase();
        if (src !== 'native') cp.querySelector('[data-custom]').value = hex;
        cp.querySelector('.bs-cp-chip').style.background = hex;
        cp.querySelector('[data-cp-sv]').setAttribute('aria-valuetext', hex.toUpperCase());
      }
      this.summary();
      if (!this._raf) this._raf = requestAnimationFrame(() => { this._raf = 0; this.draw(); });
    }

    /* ---------- mod menu: categories, search, added list, per-service details ---------- */
    modMenu(st) {
      const S = this.S;
      if (!st.options.length) return '<div class="bs-note">The upgrade list did not load. List what you want in Notes and we will price it in your ticket.</div>';
      const chosen = st.options.filter((o) => S.up.has(o.id));
      let h = `<div class="bs-msearch"><input type="search" placeholder="Search all ${st.options.length} services" value="${esc(this.find)}" data-msearch aria-label="Search services"></div>`;
      if (chosen.length) h += `<div class="bs-added"><span>Added (${chosen.length})</span>${chosen.map((o) =>
        `<button type="button" data-toggle="${o.id}" aria-label="Remove ${esc(o.name)}">${esc(o.name)}${this.qty(o) > 1 ? ' ×' + this.qty(o) : ''}<i aria-hidden="true">✕</i></button>`).join('')}</div>`;
      const f = this.find.trim().toLowerCase();
      if (f) {
        const hits = st.options.filter((o) => (o.name + ' ' + (o.note || '') + ' ' + o.group).toLowerCase().includes(f));
        return h + (hits.length ? hits.map((o) => this.modCard(o, true)).join('')
          : `<div class="bs-note">Nothing matches "${esc(this.find)}". Describe it in Notes on the Server tab and we will quote it.</div>`);
      }
      const g = st.groups.find((x) => x.label === this.cat);
      const quick = QUICK.map((id) => st.options.find((o) => o.id === id)).filter(Boolean).slice(0, 6);
      if (!g && quick.length) h += `<div class="bs-plabel">Quick picks</div><div class="bs-quick">${quick.map((o) =>
        `<button type="button" class="${S.up.has(o.id) ? 'is-on' : ''}" data-toggle="${o.id}" aria-pressed="${S.up.has(o.id)}"><b>${esc(o.name)}</b><small${this.isEst(o) ? ' class="is-est"' : ''}>${esc(this.priceText(o, true))}</small></button>`).join('')}</div><div class="bs-plabel">All categories</div>`;
      if (!g) return h + `<div class="bs-cats">${st.groups.map((x) => {
        const os = st.options.filter((o) => o.group === x.label), n = os.filter((o) => S.up.has(o.id)).length;
        return `<button type="button" class="bs-cat${n ? ' has' : ''}" data-cat="${esc(x.label)}">${ICON[x.id] || ICON.car}<b>${esc(x.label)}</b><small>${n ? n + ' added' : os.length + (os.length === 1 ? ' service' : ' services')}</small></button>`;
      }).join('')}</div>`;
      return h + `<div class="bs-cathead"><button type="button" class="bs-back" data-cat="">← All services</button><b>${ICON[g.id] || ''}${esc(g.label)}</b></div>` +
        (g.note ? `<div class="bs-note">${esc(g.note)}</div>` : '') + st.options.filter((o) => o.group === g.label).map((o) => this.modCard(o)).join('');
    }
    modCard(o, withGroup) {
      const on = this.S.up.has(o.id), sub = withGroup ? o.group + (o.note ? ' · ' + o.note : '') : o.note || '';
      return `<button type="button" class="bs-opt ${on ? 'is-on' : ''}" data-toggle="${o.id}"><span><i class="bs-chk"></i>${esc(o.name)}${o.demo ? `<i class="bs-info" data-demo="${esc(o.id)}" role="button" tabindex="0" aria-label="See ${esc(o.name)} in action" title="See it in action"><svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.4" stroke-dasharray="2.2 1.1"/><rect x="7" y="3.6" width="2" height="2" fill="currentColor"/><rect x="7" y="6.8" width="2" height="5.6" fill="currentColor"/></svg></i>` : ''}<small>${esc(sub)}</small></span><span class="bs-pr${this.isEst(o) ? ' is-est' : ''}"${this.isEst(o) ? ' title="Estimate: confirmed in your ticket"' : ''}>${esc(this.priceText(o, true))}</span></button>` +
        (on && (o.per || o.ask) ? `<div class="bs-ask">${this.askField(o)}</div>` : '');
    }
    askField(o) {
      const a = o.ask, v = this.S.ask[o.id] ?? '', id = 'bs-ask-' + o.id; let h = '';
      if (o.per) h += `<div class="bs-qty"><span>How many ${esc(o.per)}s</span><button type="button" data-qty="${o.id}:-1" aria-label="Fewer ${esc(o.per)}s">−</button><output>${this.qty(o)}</output><button type="button" data-qty="${o.id}:1" aria-label="More ${esc(o.per)}s">+</button></div>`;
      if (!a) return h;
      if (a.type === 'choice') return h + `<span class="bs-f">${esc(a.label)}</span><div class="bs-chips">${a.choices.map((c) =>
        `<button type="button" class="${v === c ? 'is-on' : ''}" data-askpick="${o.id}" data-val="${esc(c)}">${esc(c)}</button>`).join('')}</div>`;
      if (a.type === 'color') return h + `<label class="bs-f" for="${id}">${esc(a.label)}</label><div class="bs-askcol"><input id="${id}" type="color" value="${esc(v || '#ff2a1a')}" data-ask="${o.id}"><span>${esc(v ? v.toUpperCase() : 'Tap to pick a colour')}</span></div>`;
      if (a.type === 'number') return h + `<label class="bs-f" for="${id}">${esc(a.label)}</label><input id="${id}" type="number" inputmode="numeric" min="${a.min || 1}" max="${a.max || 99}" value="${esc(v)}" data-ask="${o.id}">`;
      return h + `<label class="bs-f" for="${id}">${esc(a.label)}</label><input id="${id}" type="text" maxlength="140" value="${esc(v)}" placeholder="${esc(a.placeholder || '')}" data-ask="${o.id}">`;
    }
    wheelGrid(st) {
      const f = (this.wfind || '').toLowerCase(), own = st.options.map((o) => o.name.toLowerCase()), list = this.wcat.filter((w) => !own.some((n) => n.includes(w.name.toLowerCase())) && (!f || w.name.toLowerCase().includes(f)));
      return `<div class="bs-wcat"><div class="bs-plabel">More wheels from our shop · ${this.wcat.length}</div>
        <p class="bs-small">Shown next to the car. Our team fits it to your build; fitting is quoted in your ticket.</p>
        <input type="search" class="bs-wsearch" placeholder="Search wheels (Forgiato, Vossen…)" value="${esc(this.wfind || '')}" data-wsearch>
        <div class="bs-wgrid">${list.map((w) => `<button type="button" class="bs-wcard ${this.S.pick[st.id] === w.id ? 'is-on' : ''}" data-pick="${st.id}:${w.id}" title="${esc(w.name)}"><img src="${esc(this.wbase + w.img)}" alt="" loading="lazy" width="120" height="120"><span>${esc(w.name)}</span></button>`).join('') || '<p class="bs-small">No wheels match.</p>'}</div></div>`;
    }
    peek() {
      const el = this.q('[data-bs-wpeek]'); if (!el) return;
      const st = this.cfg.steps.find((s) => s.catalog), o = st && !this.stock ? this.opt(st) : null;
      el.hidden = !(o && o.img);
      if (o && o.img) { el.querySelector('img').src = this.wbase + o.img; el.querySelector('b').textContent = o.name; el.querySelector('a').href = o.url; }
    }
    summary() {
      this.peek();
      this.root.querySelectorAll('[data-bs-total]').forEach((t) => (t.textContent = this.totalText()));
      const w = this.cfg.steps.find((s) => s.id === 'wheels');
      const n = this.S.up.size;
      this.q('[data-bs-sumtxt]').textContent = this.paintName() + (w ? ' · ' + this.opt(w).name : '') + (n ? ` · ${n} upgrade${n === 1 ? '' : 's'}` : '');
      const cart = this.q('[data-bs-cart]');
      const ready = !!(this.cfg.cart && this.cfg.cart.base_variant_id);
      cart.disabled = !ready; cart.textContent = ready ? 'Add build to cart' : 'Pricing pending';
      const note = this.q('[data-bs-pricenote]'), est = this.openEnded();
      note.hidden = !this.cfg.sample_prices && !est;
      note.textContent = this.cfg.sample_prices ? 'Sample prices — not live yet.' : 'Starting estimate. Ranged and quoted items are confirmed in your ticket before you pay.';
      this.root.querySelectorAll('[data-bs-total]').forEach((t) => t.classList.toggle('is-est', est));
    }
    onClick(e) {
      const inf = e.target.closest('[data-demo]'); if (inf && this.root.contains(inf)) { e.preventDefault(); e.stopPropagation(); this.openDemo(inf.dataset.demo); return; }
      const b = e.target.closest('button'); if (!b || !this.root.contains(b)) return;
      const d = b.dataset, S = this.S;
      if (this.stock && (d.paint || d.pick || d.hex || 'bsShuffle' in d || d.base)) this.setStock(false);
      if (d.base) { if (!this.cfg || d.base !== this.cfg.id) { const nb = this.bases.find((x) => x.id === d.base);
        if (!this.embed) history.replaceState(null, '', location.pathname + location.search.replace(/([?&])base=[^&]*&?/, '$1').replace(/[?&]$/, ''));
        this.cv.classList.add('bs-fade'); this.loadBase(nb.url); } }
      else if (!this.cfg) return; // car picker / load failure: no car yet, ignore studio controls
      else if ('spinToggle' in d) { if(this.spin.auto)this.stopAuto();else{this.spin.auto=true;b.textContent='Pause rotation';b.setAttribute('aria-pressed','true');this.draw();} }
      else if (d.view) { this.stopAuto();
        const isSpin = this.cfg.views.find((v) => v.id === d.view).type === 'spin';
        if (!isSpin) { this.spin.frames = []; this.spin.sig = ''; } // free spin frames
        else if (!this.motion.matches) this.spin.auto = true; // the 360 view starts turning on its own
        S.view = d.view; this.cv.classList.add('bs-fade'); this.render(); this.draw(); }
      else if ('bsCompare' in d) this.setStock(!this.stock);
      else if ('bsShuffle' in d) this.shuffle();
      else if ('bsShare' in d) { this.saveLink(); const done = (t) => { b.dataset.label = t; b.classList.add('is-done'); clearTimeout(b._t); b._t = setTimeout(() => b.classList.remove('is-done'), 1600); };
        navigator.clipboard ? navigator.clipboard.writeText(this.link()).then(() => done('Link copied'), () => done('Copy failed')) : done('Copy the address bar'); }
      else if ('bsFull' in d) { Promise.resolve(this.fullscreen()).finally(() => setTimeout(() => this.syncFull(), 50)); }
      else if (d.tab) { S.tab = +d.tab; this.render(); }
      else if (d.paint) { S.custom = null; S.paint = +d.paint; this.render(); this.draw(); }
      else if (d.pick) { const [st, id] = d.pick.split(':'); S.pick[st] = id; this.render(); this.draw(); }
      else if ('cat' in d) { this.cat = d.cat || null; this.find = ''; this.render(); this.q('[data-bs-pane]').scrollTop = 0; }
      else if (d.toggle) { const pv = S.view; S.up.has(d.toggle) ? S.up.delete(d.toggle) : S.up.add(d.toggle); this.keepScroll = true; this.render(); this.showFor(d.toggle, pv); }
      else if (d.qty) { const [id, step] = d.qty.split(':'); S.qty[id] = Math.min(20, Math.max(1, (S.qty[id] || 1) + Number(step))); this.keepScroll = true; this.render(); }
      else if ('askpick' in d) { const pv = S.view; S.ask[d.askpick] = S.ask[d.askpick] === d.val ? '' : d.val; this.keepScroll = true; this.render(); this.showFor(d.askpick, pv); }
      else if (d.hex) { this.setCustom(d.hex, 'swatch'); }
      else if (d.rot) { this.rotateTo(this.S.frame + Number(d.rot)); }
      else if ('bsQuote' in d) this.openSheet('quote');
      else if ('bsCart' in d) this.addToCart();
      else if ('bsClose' in d) { this.q('[data-bs-modal]').hidden = true; this.q('.bs-demo video')?.pause(); }
      else if ('bsCopy' in d) { this.track('capos_build_sheet_copied', { build_id: this.lastId }); navigator.clipboard?.writeText(this.sheetText).then(() => (b.textContent = 'Copied')); }
    }
    // random paint and a random pick for every single-choice step (wheels); upgrades and server details stay as they are
    shuffle() {
      const S = this.S, n = this.cfg.paints.length; let p = S.paint;
      if (n > 1) while (p === S.paint) p = Math.floor(Math.random() * n);
      S.custom = null; S.paint = p;
      for (const st of this.cfg.steps) if (st.type === 'single') S.pick[st.id] = st.options[Math.floor(Math.random() * st.options.length)].id;
      this.render(); this.draw();
    }
    fullscreen() {
      const el = this.q('.bs-canvas');
      if (document.fullscreenElement) return document.exitFullscreen();
      if (el.requestFullscreen) el.requestFullscreen().catch(() => el.classList.toggle('bs-max'));
      else el.classList.toggle('bs-max'); // iPhone Safari: no element fullscreen, fill the window instead
    }
    // label the button for the state it toggles to; Escape leaves the fill-the-window fallback
    syncFull() { const on = !!document.fullscreenElement || this.q('.bs-canvas').classList.contains('bs-max'), b = this.q('[data-bs-full]');
      if (b) { b.dataset.label = on ? 'Exit full screen' : 'Full screen'; b.setAttribute('aria-label', on ? 'Exit full screen' : 'Full screen preview'); } }
    onInput(e) {
      const t = e.target; if (!this.cfg) return;
      if ('custom' in t.dataset) { if (/^#[0-9a-f]{6}$/i.test(t.value)) this.setCustom(t.value, 'native'); }
      else if ('cpHue' in t.dataset) {
        let [, sat, v] = this.hsv; if (v < 0.15) v = 0.75; if (sat < 0.1) sat = 0.85; // hue means nothing on black/white: give it some colour
        this.hsv = [+t.value, sat, v]; this.setCustom(hsvHex(...this.hsv), 'hue');
      }
      else if ('cpHex' in t.dataset) { let v = t.value.trim(); if (v[0] !== '#') v = '#' + v; if (/^#[0-9a-f]{6}$/i.test(v)) { this.setCustom(v, 'hex'); this.pushRecent(v.toLowerCase()); } }
      else if ('rotRange' in t.dataset) this.rotateTo(+t.value);
      else if ('wsearch' in t.dataset) { this.wfind = t.value; this.keepScroll = true; this.render(); const q = this.q('[data-wsearch]'); q.focus(); q.setSelectionRange(q.value.length, q.value.length); }
      else if ('msearch' in t.dataset) { this.find = t.value; this.render(); const q = this.q('[data-msearch]'); q.focus(); q.setSelectionRange(q.value.length, q.value.length); }
      else if (t.dataset.ask) { this.S.ask[t.dataset.ask] = t.value; const sp = t.nextElementSibling; if (t.type === 'color' && sp) sp.textContent = t.value.toUpperCase(); }
      else if (t.dataset.field) this.S[t.dataset.field] = t.value;
    }

    /* ---------- build sheet + order ---------- */
    buildRows() {
      const rows = [['Vehicle', this.cfg.title + (this.cfg.subtitle ? ' ' + this.cfg.subtitle : ''), money(this.cfg.base_price)],
                    ['Paint', this.paintName() + ' (preview, set in-game)', '—']];
      for (const st of this.cfg.steps) {
        if (st.type === 'single') { const o = this.opt(st); rows.push([st.label, o.name, o.quote ? 'Quoted in ticket' : o.price ? money(o.price) : 'Included']); }
        if (st.type === 'multi') for (const o of st.options.filter((x) => this.S.up.has(x.id))) {
          const detail = (this.S.ask[o.id] || '').toString().trim();
          rows.push([o.group || st.label, o.name + (this.qty(o) > 1 ? ' ×' + this.qty(o) : '') + (detail ? ` (${o.ask.label}: ${detail})` : ''), this.priceText(o, false)]);
        }
      }
      return rows;
    }
    buildId() { return this.cfg.id_prefix + '-' + (1000 + Math.floor(Math.random() * 9000)); }
    // lead signal for ad tracking: Shopify customer events (a Customer Events pixel can forward it); no customer details are sent
    track(name, extra) {
      try { window.Shopify?.analytics?.publish?.(name, { base: this.cfg.id, car: this.cfg.title, value: this.total(), currency: this.cfg.currency || 'USD', upgrades: this.S.up.size, estimate: this.openEnded(), ...extra }); } catch (e) { /* analytics unavailable */ }
    }
    openSheet(kind) {
      const id = this.buildId(), rows = this.buildRows(), S = this.S;
      this.lastId = id; this.track('capos_build_request', { build_id: id });
      let img;
      try {
        const c = document.createElement('canvas'); c.width = this.W; c.height = this.H; const x = c.getContext('2d');
        if (this.cfg.lit !== 'room' && this.garage?.complete && this.garage.naturalWidth) x.drawImage(this.garage, 0, 0, this.W, this.H);
        x.drawImage(this.cv, 0, 0); img = c.toDataURL('image/webp', 0.85);
      } catch (e) { img = this.cv.toDataURL('image/webp', 0.85); }
      const server = [['Discord', S.discord || 'required'], ['Framework', S.fw], ['Limits', S.limits || 'none given'], ['Notes', S.notes || '—']];
      this.sheetText = [`${id} — ${kind === 'quote' ? 'Quote request' : 'Build'}`, ...rows.map((r) => `${r[0]}: ${r[1]} (${r[2]})`),
        ...server.map((r) => `${r[0]}: ${r[1]}`), `Total: ${this.totalText()}${this.openEnded() ? ' (some items priced in your ticket)' : ''}`,
        ...(this.cfg.sample_prices ? ['Sample prices — not live yet.'] : []), `Build link: ${this.link()}`].join('\n');
      this.q('[data-bs-sheet]').innerHTML = `
        <button type="button" class="bs-close" data-bs-close>Close</button>
        <div class="bs-id">${id}</div><h2>${kind === 'quote' ? 'Quote request — build sheet' : 'Build sheet'}</h2>
        <img src="${img}" alt="Your build preview">
        <table>${rows.map((r) => `<tr><td>${esc(r[0])}</td><td>${esc(r[1])}</td><td>${esc(r[2])}</td></tr>`).join('')}
        ${server.map((r) => `<tr><td>${esc(r[0])}</td><td colspan="2">${esc(r[1])}</td></tr>`).join('')}
        <tr class="bs-tot"><td>Total</td><td></td><td>${this.totalText()}</td></tr></table>
        ${this.openEnded() ? '<p class="bs-small">Items marked Quote or "from" are confirmed in your ticket.</p>' : ''}
        ${this.cfg.sample_prices ? '<p class="bs-small">Sample prices — not live yet.</p>' : ''}
        <p class="bs-small">Copy this sheet and open a ticket in our Discord so the team can quote it.</p>
        <div class="bs-btns"><button type="button" class="bs-btn bs-ghost" data-bs-copy>Copy build sheet</button>
        ${this.cfg.quote_url ? `<a class="bs-btn" href="${esc(this.cfg.quote_url)}" target="_blank" rel="noopener" data-bs-ticket>Open a ticket</a>` : ''}</div>`;
      this.q('[data-bs-modal]').hidden = false;
    }
    // "i" on a service: short looping clip of the part working (demo = video or gif URL on the option in build-studio-mods.json)
    openDemo(id) {
      const o = this.cfg.steps.flatMap((st) => st.options || []).find((x) => x.id === id); if (!o || !o.demo) return;
      const vid = /\.(mp4|webm|mov)(\?|$)/i.test(o.demo);
      this.q('[data-bs-sheet]').innerHTML = `
        <button type="button" class="bs-close" data-bs-close>Close</button>
        <h2>${esc(o.name)}</h2>${o.note ? `<p class="bs-small">${esc(o.note)}</p>` : ''}
        <div class="bs-demo">${vid ? `<video src="${esc(o.demo)}" autoplay loop muted playsinline></video>` : `<img src="${esc(o.demo)}" alt="${esc(o.name)} in action">`}</div>
        <p class="bs-small">${esc(this.priceText(o, true))} · Recorded in-game.</p>`;
      this.q('[data-bs-modal]').hidden = false;
    }
    async addToCart() {
      const c = this.cfg.cart; if (!c || !c.base_variant_id) return;
      const id = this.buildId(), S = this.S;
      const props = { 'Build ID': id, Paint: this.paintName(), Discord: S.discord, Framework: S.fw, Limits: S.limits, Notes: S.notes, _build_link: this.link() };
      for (const [k, v, p] of this.buildRows().slice(2)) props[k] = props[k] ? props[k] + ', ' + v : v;
      const items = [{ id: c.base_variant_id, quantity: 1, properties: props }];
      for (const st of this.cfg.steps) {
        const chosen = st.type === 'single' ? [this.opt(st)] : st.type === 'multi' ? st.options.filter((o) => S.up.has(o.id)) : [];
        for (const o of chosen) if (o.variant_id) items.push({ id: o.variant_id, quantity: 1, properties: { 'Build ID': id } });
      }
      const r = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (r.ok) location.href = (window.Shopify?.routes?.root || '/') + 'cart';
    }
  }

  const boot = () => document.querySelectorAll('[data-build-studio]:not([data-bs-ready])').forEach((el) => { el.dataset.bsReady = '1'; el.buildStudio = new BuildStudio(el); });
  boot();
  document.addEventListener('shopify:section:load', boot);
})();
